import { useCallback } from "react";
import {
  ContentDeviceError,
  registerContentDevice,
} from "../api/contentDeviceApi";
import { useDeviceStore } from "../views/tutorV2/store";
import { useAuth } from "../views/tutorV2/hooks/useAuth";
import { useUserQuery } from "../views/tutorV2/hooks/queries/useUserQuery";
import {
  isDeviceCheckValid,
  markDeviceChecked,
} from "../utils/contentDeviceCache";

interface EnsureDeviceRegisteredParams {
  /** 대화 단위 식별자 (예: "tutor:123", "socrates:45") */
  conversationKey: string;
  /** 기기 해제 후 이어서 실행할 전송 동작 (Promise 반환 시 모달이 완료까지 대기) */
  onRetry: () => void | Promise<void>;
}

/**
 * 채팅 전송 시점 기기 등록 판정 훅
 *
 * 기획(업무 #11104) 기준 흐름:
 * - 이미 등록된 기기이거나 슬롯 여유가 있으면 그대로 전송
 * - 슬롯이 가득 찬 신규 기기면 초과 안내 노출 (전송 중단, 입력 메시지 유지)
 * - accessToken 만료(401)면 로그인 화면으로 이동 (현재 경로로 복귀)
 *
 * 판정은 뷰어와 동일하게 대화 단위로 1회만 수행한다.
 * (같은 대화를 이어가는 동안에는 캐시된 결과를 재사용)
 * 재판정 시점: 대화 전환 / 새로고침 / 로그아웃 / 상한 경과.
 * 초과 안내에서 기기를 해제한 경우는 실패가 캐시되지 않아 자동으로 재판정된다.
 *
 * 기기 목록/해제 화면은 통합마이페이지가 담당하므로,
 * 초과 안내에서 기기 관리로 이동한 뒤 "다시 시도"로 재판정한다.
 */
export const useDeviceGuard = () => {
  const { redirectToLogin } = useAuth();
  const { user } = useUserQuery();

  // 액션은 참조가 고정되어 있어 셀렉터로 구독해도 리렌더가 발생하지 않는다
  const openLimitModal = useDeviceStore((state) => state.openLimitModal);
  const closeLimitModal = useDeviceStore((state) => state.closeLimitModal);
  const setIsChecking = useDeviceStore((state) => state.setIsChecking);
  // 판정 중에는 입력/전송을 막아야 하므로 이 값만 구독한다
  const isCheckingDevice = useDeviceStore((state) => state.isChecking);

  const userId = user?.id ?? null;

  /**
   * 기기 등록 여부를 확인한다.
   *
   * @returns 전송을 진행해도 되면 true
   */
  const ensureDeviceRegistered = useCallback(
    async ({
      conversationKey,
      onRetry,
    }: EnsureDeviceRegisteredParams): Promise<boolean> => {
      // 같은 대화에서 이미 판정을 통과했으면 서버에 다시 묻지 않는다
      if (isDeviceCheckValid(userId, conversationKey)) return true;

      // 중복 전송 방지: 판정 왕복 동안 전송 버튼이 열려 있어 연타가 가능하다.
      // 구독값 대신 최신 상태를 직접 읽어 같은 틱의 재진입까지 막는다.
      if (useDeviceStore.getState().isChecking) return false;

      setIsChecking(true);

      try {
        await registerContentDevice("AIR");
        markDeviceChecked(userId, conversationKey);

        // 재시도로 들어온 경우: 판정을 통과했으므로 안내를 닫는다.
        // (모달이 따로 register를 호출하지 않도록 하여 중복 호출을 없앤다)
        if (useDeviceStore.getState().limitModalOpen) closeLimitModal();

        return true;
      } catch (error) {
        if (error instanceof ContentDeviceError) {
          // 인증 만료: 로그인 화면으로 이동 (별도 재발급 로직 없음)
          if (error.isUnauthenticated) {
            redirectToLogin();
            return false;
          }

          // 슬롯 초과: 초과 안내 노출 후 전송 중단
          // 사용자가 해제할 기기를 고르면 모달에서 교체(replace) 후 전송을 재개한다
          if (error.isSlotLimitExceeded) {
            openLimitModal({
              activeDevices: error.activeDevices,
              limit: error.limit,
              onResume: onRetry,
              userId,
              conversationKey,
            });
            return false;
          }
        }

        // 그 외(네트워크/서버 오류)에는 채팅을 막지 않는다.
        // 기기 확인 실패로 서비스 전체가 중단되는 것을 피하기 위한 처리.
        // 실패는 캐시하지 않으므로 다음 전송에서 다시 판정한다.
        console.error("기기 등록 확인 실패:", error);
        return true;
      } finally {
        setIsChecking(false);
      }
    },
    [userId, redirectToLogin, openLimitModal, closeLimitModal, setIsChecking]
  );

  return { ensureDeviceRegistered, isCheckingDevice };
};

export default useDeviceGuard;
